import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Box, Typography, Button, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix pour les icônes de marqueurs Leaflet avec Webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import iconRetina from 'leaflet/dist/images/marker-icon-2x.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: iconRetina,
  iconUrl: icon,
  shadowUrl: iconShadow,
});

// Composant interne pour recentrer la carte quand les sites changent
function MapBoundsUpdater({ sites }) {
  const map = useMap();
  const prevSiteIdsRef = useRef('');

  useEffect(() => {
    const siteIds = sites.map(s => s.id).sort().join(',');
    if (siteIds === prevSiteIdsRef.current) return;
    prevSiteIdsRef.current = siteIds;

    if (sites.length === 0) return;

    if (sites.length === 1) {
      map.setView(
        [parseFloat(sites[0].latitude), parseFloat(sites[0].longitude)],
        13,
        { animate: true }
      );
    } else {
      const bounds = L.latLngBounds(
        sites.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])
      );
      map.fitBounds(bounds, { padding: [30, 30], animate: true });
    }
  }, [sites, map]);

  return null;
}

function SiteMap({ sites, onSiteClick }) {
  const { t } = useTranslation();

  // Filtrer les sites qui ont des coordonnées GPS
  const sitesWithCoordinates = sites.filter(
    site => site.latitude && site.longitude &&
    !isNaN(parseFloat(site.latitude)) && !isNaN(parseFloat(site.longitude))
  );

  // Calculer le centre de la carte en fonction des sites
  const getMapCenter = () => {
    if (sitesWithCoordinates.length === 0) {
      // Centre par défaut (Paris)
      return [48.8566, 2.3522];
    }

    if (sitesWithCoordinates.length === 1) {
      return [
        parseFloat(sitesWithCoordinates[0].latitude),
        parseFloat(sitesWithCoordinates[0].longitude)
      ];
    }

    // Calculer le centre moyen de tous les sites
    const avgLat = sitesWithCoordinates.reduce((sum, site) => sum + parseFloat(site.latitude), 0) / sitesWithCoordinates.length;
    const avgLng = sitesWithCoordinates.reduce((sum, site) => sum + parseFloat(site.longitude), 0) / sitesWithCoordinates.length;

    return [avgLat, avgLng];
  };

  // Calculer le niveau de zoom optimal
  const getOptimalZoom = () => {
    if (sitesWithCoordinates.length === 0) return 6;
    if (sitesWithCoordinates.length === 1) return 13;

    // Calculer la distance maximale entre les sites pour déterminer le zoom
    const lats = sitesWithCoordinates.map(s => parseFloat(s.latitude));
    const lngs = sitesWithCoordinates.map(s => parseFloat(s.longitude));

    const latSpan = Math.max(...lats) - Math.min(...lats);
    const lngSpan = Math.max(...lngs) - Math.min(...lngs);
    const maxSpan = Math.max(latSpan, lngSpan);

    // Déterminer le zoom en fonction de l'étendue
    if (maxSpan > 10) return 4;
    if (maxSpan > 5) return 5;
    if (maxSpan > 2) return 6;
    if (maxSpan > 1) return 7;
    if (maxSpan > 0.5) return 8;
    return 10;
  };

  return (
    <Box sx={{ height: '600px', width: '100%', position: 'relative' }}>
      {sitesWithCoordinates.length === 0 ? (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            borderRadius: 2,
            p: 4
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('noCoordinates')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aucun site n'a de coordonnées GPS renseignées
          </Typography>
        </Box>
      ) : (
        <MapContainer
          center={getMapCenter()}
          zoom={getOptimalZoom()}
          style={{ height: '100%', width: '100%', borderRadius: '8px' }}
        >
          <MapBoundsUpdater sites={sitesWithCoordinates} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {sitesWithCoordinates.map((site) => (
            <Marker
              key={site.id}
              position={[parseFloat(site.latitude), parseFloat(site.longitude)]}
            >
              <Popup>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {site.nom_site}
                  </Typography>

                  {site.ville && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      📍 {site.ville}, {site.pays}
                    </Typography>
                  )}

                  {site.usage_site && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      🏛️ {site.usage_site}
                    </Typography>
                  )}

                  {site.budget_exploitation && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`Budget: ${parseInt(site.budget_exploitation).toLocaleString('fr-FR')} ${site.devise_pays || 'EUR'}`}
                        size="small"
                        sx={{ bgcolor: 'primary.light', color: 'white', fontSize: '0.75rem' }}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => onSiteClick(site.id)}
                  >
                    Ouvrir le site
                  </Button>
                </Box>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      )}

      {/* Légende */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          p: 1.5,
          borderRadius: 1,
          boxShadow: 2,
          zIndex: 1000
        }}
      >
        <Typography variant="caption" fontWeight={600}>
          {sitesWithCoordinates.length} site(s) géolocalisé(s)
        </Typography>
      </Box>
    </Box>
  );
}

export default SiteMap;
